import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);
export const isAdmin = (u) => ["admin", "superadmin"].includes(u?.role);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/admin/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    const id = api.interceptors.response.use((r) => r, (e) => {
      const url = e.config?.url || "";
      if (e.response?.status === 401 && url.startsWith("/admin") && !url.startsWith("/admin/auth")) setUser(false);
      return Promise.reject(e);
    });
    return () => api.interceptors.response.eject(id);
  }, []);
  const logout = async () => { await api.post("/admin/auth/logout").catch(() => {}); setUser(false); };
  return <AuthCtx.Provider value={{ user, setUser, logout }}>{children}</AuthCtx.Provider>;
}

export function RequireAuth({ children, adminOnly = false, superOnly = false }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#080706]" />;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (superOnly && user.role !== "superadmin") return <Navigate to="/admin" replace />;
  if (adminOnly && !isAdmin(user)) return <Navigate to="/admin" replace />;
  return children;
}

const Shell = ({ title, sub, children }) => (
  <div className="min-h-screen bg-[#080706] flex items-center justify-center px-5 py-12">
    <div className="w-full max-w-[420px] reveal">
      <img src="/emblem-square.png" alt="" className="w-16 h-16 rounded-full mx-auto" />
      <p className="eyebrow text-center mt-6">Backoffice</p>
      <h1 className="text-3xl text-center text-[#F3EFE6] mt-3">{title}</h1>
      {sub && <p className="text-sm text-center text-[#A39E93] mt-3">{sub}</p>}
      <div className="mt-10 bg-[#161512] border border-[#282520] p-7">{children}</div>
    </div>
  </div>
);

export function AdminLogin() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("login");
  const [f, setF] = useState({ email: "", password: "", new1: "", new2: "", code: "" });
  const [qr, setQr] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { if (step === "setup_2fa") api.get("/admin/auth/2fa/setup").then((r) => setQr(r.data)).catch((e) => setErr(errMsg(e))); }, [step]);
  if (user) return <Navigate to="/admin" replace />;

  const run = (fn) => async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { await fn(); } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  const login = run(async () => setStep((await api.post("/admin/auth/login", { email: f.email, password: f.password })).data.next));
  const changePwd = run(async () => {
    if (f.new1 !== f.new2) throw { response: { data: { detail: "As palavras-passe não coincidem." } } };
    setStep((await api.post("/admin/auth/change-password", { new_password: f.new1 })).data.next);
  });
  const verify = run(async () => { const r = await api.post("/admin/auth/2fa/verify", { code: f.code }); setUser(r.data); nav("/admin"); });

  const Err = () => err ? <p className="text-sm text-[#e0937f] mt-4" role="alert" data-testid="admin-login-error">{err}</p> : null;
  if (step === "change_password") return (
    <Shell title="Nova palavra-passe" sub="A palavra-passe provisória tem de ser trocada no primeiro acesso.">
      <form onSubmit={changePwd} className="space-y-4" data-testid="admin-change-password-form">
        <input type="password" className="afield" placeholder="Nova palavra-passe (mín. 12, letras e números)" value={f.new1} onChange={set("new1")} data-testid="admin-new-password" autoComplete="new-password" />
        <input type="password" className="afield" placeholder="Repetir palavra-passe" value={f.new2} onChange={set("new2")} data-testid="admin-new-password-confirm" autoComplete="new-password" />
        <Err /><button className="btn-gold w-full" disabled={busy} data-testid="admin-change-password-submit">Guardar e continuar</button>
      </form>
    </Shell>
  );
  if (step === "setup_2fa" || step === "verify_2fa") return (
    <Shell title={step === "setup_2fa" ? "Configurar 2FA" : "Verificação em dois passos"} sub={step === "setup_2fa" ? "Leia o código QR com a sua aplicação autenticadora (Google Authenticator, 1Password, Authy…)." : "Introduza o código de 6 dígitos da sua aplicação autenticadora."}>
      <form onSubmit={verify} className="space-y-4" data-testid="admin-2fa-form">
        {step === "setup_2fa" && qr && (
          <div className="text-center space-y-3" data-testid="admin-2fa-setup">
            <img src={qr.qr} alt="Código QR 2FA" className="w-48 h-48 mx-auto bg-white p-2" />
            <p className="text-[11px] text-[#A39E93]">Ou introduza a chave manualmente:</p>
            <code className="block text-xs text-[#E6D5AC] break-all" data-testid="admin-2fa-secret">{qr.secret}</code>
          </div>
        )}
        <input inputMode="numeric" maxLength={6} className="afield text-center tracking-[0.5em] text-lg" placeholder="000000" value={f.code} onChange={set("code")} autoFocus data-testid="admin-2fa-code" autoComplete="one-time-code" />
        <Err /><button className="btn-gold w-full" disabled={busy || f.code.length < 6} data-testid="admin-2fa-submit"><ShieldCheck className="w-4 h-4" /> Verificar</button>
      </form>
    </Shell>
  );
  return (
    <Shell title="Iniciar sessão">
      <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
        <input type="email" className="afield" placeholder="Email" value={f.email} onChange={set("email")} autoComplete="username" data-testid="admin-login-email" />
        <input type="password" className="afield" placeholder="Palavra-passe" value={f.password} onChange={set("password")} autoComplete="current-password" data-testid="admin-login-password" />
        <Err /><button className="btn-gold w-full" disabled={busy || !f.email || !f.password} data-testid="admin-login-submit">Continuar</button>
      </form>
    </Shell>
  );
}
